/**
 * Whether a tenant's people must sign in with a second factor (migration 986).
 *
 * The switch is drawn only for someone who may change it (a tenant
 * administrator or a platform admin). Turning it on needs their own session to
 * have a second factor — otherwise they would lock themselves out — so until
 * then the panel says so and offers to set one up, instead of a switch that
 * would refuse.
 */

import { useCallback, useEffect, useState } from 'react';
import { Panel, Switch, Callout } from '../../../../components/ds';
import { btnPrimary, btnSecondary } from '../../../../components/mono/primitives';
import { useUserStore } from '../../../../stores/prioritization/userStore';
import {
  fetchSecondFactorRequirement,
  fetchTenantSecondFactor,
  setTenantSecondFactor,
  type TenantSecondFactor,
} from '../../../../lib/identityService';
import { AuthenticatorCodeForm } from '../../../../components/auth/AuthenticatorCodeForm';

export function SecondFactorRequirementPanel({
  tenantId,
  tenantName,
  isPlatformAdmin,
}: {
  tenantId: string;
  tenantName: string;
  isPlatformAdmin: boolean;
}) {
  const userId = useUserStore((s) => s.userId);
  const [setting, setSetting] = useState<TenantSecondFactor | null>(null);
  const [ownLevel, setOwnLevel] = useState<'aal1' | 'aal2'>('aal1');
  const [settingUp, setSettingUp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [s, req] = await Promise.all([fetchTenantSecondFactor(tenantId, userId, isPlatformAdmin), fetchSecondFactorRequirement()]);
      setSetting(s);
      setOwnLevel(req.level);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read how this tenant signs in.');
    }
  }, [tenantId, userId, isPlatformAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!setting) return error ? <Callout tone="coral">{error}</Callout> : null;

  const change = async (required: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await setTenantSecondFactor(tenantId, required);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change it.');
    } finally {
      setSaving(false);
    }
  };

  const needsOwnFactor = setting.canChange && !setting.required && ownLevel !== 'aal2';

  return (
    <Panel label="Sign-in" title={`How people in ${tenantName} sign in`}>
      <div className="space-y-4">
        <p className="text-sm text-[var(--text-sec)]">
          {setting.required
            ? `Everyone in ${tenantName} signs in with a password and a code from an authenticator app. Until they enter the code they see nothing of this tenant’s workspaces.`
            : `People in ${tenantName} sign in with a password or an email link. A second factor is not required.`}
        </p>

        {setting.canChange && !needsOwnFactor && (
          <Switch
            checked={setting.required}
            disabled={saving}
            onChange={(next) => void change(next)}
            label="Require a second factor"
            description="Each person sets up an authenticator app the next time they sign in, then enters a code every time."
          />
        )}

        {needsOwnFactor && !settingUp && (
          <Callout
            tone="mustard"
            title="Sign in with a second factor yourself first"
            action={
              <button type="button" {...btnPrimary()} onClick={() => setSettingUp(true)}>
                Set up mine
              </button>
            }
          >
            Requiring one before you have one would lock you out. Set up an authenticator app for your own sign-in; the switch appears here when you have.
          </Callout>
        )}

        {needsOwnFactor && settingUp && (
          <div className="max-w-sm space-y-3">
            <AuthenticatorCodeForm
              onVerified={() => {
                setSettingUp(false);
                void load();
              }}
            />
            <button type="button" {...btnSecondary()} onClick={() => setSettingUp(false)}>
              Not now
            </button>
          </div>
        )}

        {!setting.canChange && (
          <p className="text-xs text-[var(--text-ter)]">An administrator of {tenantName} decides this.</p>
        )}

        {error && <Callout tone="coral">{error}</Callout>}
      </div>
    </Panel>
  );
}
